import { useCallback, useMemo } from 'react';

import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  useLazySearchSchedulesQuery,
  useLazySearchShipmentGroupsQuery,
} from '@/app/services/lookups';
import { ColDef } from '@/grid/types';
import {
  dateOnlyFormatter,
  dateOnlyParser,
} from '@/grid/value-formatters/dateOnlyFormatter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AutocompleteCellEditor } from '@/pages/bulk-donor-scheduling/components/autocomplete-cell-editor/index';
import { SEARCH_YARDS } from '@/types.gql';

import { SemenAutocompleteCellEditor } from '../components/semen-autocomplete-cell-editor/index';
import { ROW_NUMBER_COLUMN } from '../utils/row-number-column';
import { BulkDonorScheduleInput } from '../utils/schema';

function useSearchYards() {
  const [searchYards] = useLazyQuery(SEARCH_YARDS);

  return useCallback(
    async (query: string) => {
      const search = query.trim().toLowerCase();
      const { data } = await searchYards({
        variables: {
          search,
          selectionLimit: 100,
        },
      });
      const results =
        data?.yards?.items?.filter((y) => y.name).map((y) => y.name!) ?? [];
      return results.filter((yard) => yard.toLowerCase().includes(search));
    },
    [searchYards],
  );
}

export function useColumns({
  fertilizationSiteId,
}: {
  fertilizationSiteId: string | undefined;
}) {
  const { t } = useTranslation();
  const fixBulkDonorScheduleDateFormat = useFeatureFlag(
    'fix_bulk_donor_schedule_date_format',
  );

  const [searchSchedules] = useLazySearchSchedulesQuery();
  const [searchShipmentGroups] = useLazySearchShipmentGroupsQuery();
  const searchYardsMemo = useSearchYards();

  const searchSchedulesMemo = useCallback(
    async (query: string) => {
      const { data } = await searchSchedules({
        search: query,
        selectionLimit: 100,
      });
      const results = data?.map((s) => s.name!) || [];
      return results.filter((schedule) =>
        schedule.toLowerCase().includes(query.toLowerCase()),
      );
    },
    [searchSchedules],
  );

  const searchShipmentGroupsMemo = useCallback(
    async (query: string) => {
      const { data } = await searchShipmentGroups({
        search: query,
        selectionLimit: 100,
      });
      const results = data?.map((sg) => sg.name!) || [];
      return results.filter((group) =>
        group.toLowerCase().includes(query.toLowerCase()),
      );
    },
    [searchShipmentGroups],
  );

  return useMemo<ColDef<BulkDonorScheduleInput>[]>(
    () => [
      ROW_NUMBER_COLUMN,
      {
        headerName: t('bulkDonorScheduling.Account_Code'),
        field: 'accountCode',
        initialWidth: 110,
        valueSetter(params) {
          if (!params.data) {
            return false;
          }

          const newValue = params.newValue;
          params.data.accountCode = newValue;

          if (!params.data.billAccountCode) {
            params.data.billAccountCode = newValue;
          }

          return true;
        },
      },
      {
        headerName: t('bulkDonorScheduling.Bill_Account'),
        field: 'billAccountCode',
        initialWidth: 110,
      },
      {
        headerName: t('bulkDonorScheduling.Breed'),
        field: 'donorBreed',
        initialWidth: 60,
      },
      {
        headerName: t('bulkDonorScheduling.Donor_ID'),
        field: 'donorNameToMatch',
        initialWidth: 76,
      },
      {
        headerName: t('bulkDonorScheduling.Registration'),
        field: 'donorRegistrationNumber',
        initialWidth: 124,
      },
      {
        headerName: t('bulkDonorScheduling.Pedigree_Name'),
        field: 'pedigreeName',
        initialWidth: 117,
      },
      {
        headerName: t('bulkDonorScheduling.Pregnant'),
        field: 'pregnant',
        initialWidth: 80,
      },
      {
        headerName: t('bulkDonorScheduling.Birth_Date'),
        field: 'birthDate',
        initialWidth: 84,
        valueFormatter: fixBulkDonorScheduleDateFormat
          ? dateOnlyFormatter
          : undefined,
        valueParser: fixBulkDonorScheduleDateFormat
          ? dateOnlyParser
          : undefined,
        cellEditorParams: fixBulkDonorScheduleDateFormat
          ? { useFormatter: true }
          : undefined,
      },
      {
        headerName: t('bulkDonorScheduling.Program_Name'),
        field: 'programName',
        initialWidth: 84,
      },
      {
        headerName: t('bulkDonorScheduling.Tpi'),
        field: 'tpi',
        initialWidth: 60,
        valueParser: (params) => {
          if (
            params.newValue === '' ||
            params.newValue === null ||
            params.newValue === undefined
          ) {
            return null;
          }
          const num = Number(params.newValue);
          return isNaN(num) ? null : num;
        },
      },
      {
        headerName: t('bulkDonorScheduling.Net_Merit'),
        field: 'nm',
        initialWidth: 60,
        valueParser: (params) => {
          if (
            params.newValue === '' ||
            params.newValue === null ||
            params.newValue === undefined
          ) {
            return null;
          }
          const num = Number(params.newValue);
          return isNaN(num) ? null : num;
        },
      },
      {
        headerName: t('bulkDonorScheduling.Opu_Program'),
        field: 'opuProgram',
        initialWidth: 90,
        headerTooltip: 'Normal, LOPU, Tween',
      },
      {
        headerName: t('bulkDonorScheduling.Tattoo'),
        field: 'tattoo',
        initialWidth: 84,
      },
      {
        headerName: t('bulkDonorScheduling.Received_Date'),
        field: 'received',
        initialWidth: 84,
        valueFormatter: fixBulkDonorScheduleDateFormat
          ? dateOnlyFormatter
          : undefined,
        valueParser: fixBulkDonorScheduleDateFormat
          ? dateOnlyParser
          : undefined,
        cellEditorParams: fixBulkDonorScheduleDateFormat
          ? { useFormatter: true }
          : undefined,
      },
      {
        headerName: t('bulkDonorScheduling.Yard_Code'),
        field: 'yardCode',
        initialWidth: 84,
        cellEditor: AutocompleteCellEditor,
        cellEditorPopup: true,
        cellEditorParams: {
          searchFunction: searchYardsMemo,
        },
      },
      {
        headerName: t('bulkDonorScheduling.Semen'),
        field: 'semen',
        initialWidth: 60,
        cellEditor: SemenAutocompleteCellEditor,
        cellEditorPopup: true,
        cellEditorParams: {
          fertilizationSiteId,
        },
        headerTooltip: 'References a specific semen record',
      },
      {
        headerName: t('bulkDonorScheduling.Sire'),
        field: 'sire',
        initialWidth: 60,
        headerTooltip: 'Sire name only (not attached to a semen record)',
      },
      {
        headerName: t('bulkDonorScheduling.Sire_Breed'),
        field: 'sireBreed',
        initialWidth: 84,
      },
      {
        headerName: t('bulkDonorScheduling.Sire_Registration'),
        field: 'sireRegistrationNumber',
        initialWidth: 84,
      },
      {
        headerName: t('bulkDonorScheduling.Collection_Code'),
        field: 'collectionCode',
        initialWidth: 120,
      },
      {
        headerName: t('bulkDonorScheduling.Semen_Type'),
        field: 'semenType',
        initialWidth: 99,
        headerTooltip: 'Conv, SF F, SF M, RS F, RS M',
      },
      {
        headerName: t('bulkDonorScheduling.NAAB'),
        field: 'naab',
        initialWidth: 55,
      },
      {
        headerName: t('bulkDonorScheduling.Cane'),
        field: 'cane',
        initialWidth: 60,
      },
      {
        headerName: t('bulkDonorScheduling.Donor_Location'),
        field: 'donorLocation',
        initialWidth: 100,
      },
      {
        headerName: t('bulkDonorScheduling.Schedule'),
        field: 'scheduleCode',
        initialWidth: 81,
        cellEditor: AutocompleteCellEditor,
        cellEditorPopup: true,
        cellEditorParams: {
          searchFunction: searchSchedulesMemo,
        },
      },
      {
        headerName: t('bulkDonorScheduling.Pool'),
        field: 'pool',
        initialWidth: 51,
        headerTooltip: 'Y, N',
      },
      {
        headerName: t('bulkDonorScheduling.Aspiration_Comment'),
        field: 'aspirationComment',
        initialWidth: 159,
      },
      {
        headerName: t('bulkDonorScheduling.Ship'),
        field: 'ship',
        initialWidth: 60,
        headerTooltip: 'Ship Straw All, Ship Straw, Ship Day 5, Ship, Ship All',
      },
      {
        headerName: t('bulkDonorScheduling.Shipment_Group'),
        field: 'shipmentGroup',
        initialWidth: 60,
        cellEditor: AutocompleteCellEditor,
        cellEditorPopup: true,
        cellEditorParams: {
          searchFunction: searchShipmentGroupsMemo,
        },
      },
      {
        headerName: t('bulkDonorScheduling.Freeze'),
        field: 'freeze',
        initialWidth: 60,
        headerTooltip:
          'Freeze, Freeze ONLY, Freeze 1, Freeze 2, Freeze 3, Freeze 4, Freeze 5, Freeze 6, Freeze 7, Freeze 8, Freeze 9, Freeze 10',
      },
      {
        headerName: t('bulkDonorScheduling.Freeze_Method'),
        field: 'freezeMethod',
        initialWidth: 60,
        headerTooltip:
          '1.5M EG, 1.5M EG with suc, 10% Glycerol, Vitrification, DT-VIT, IVB, Vitro Origen, AG, ST-VIT, ',
      },
      {
        headerName: t('bulkDonorScheduling.Freeze_Instructions'),
        field: 'freezeInstructions',
        initialWidth: 100,
      },
      {
        headerName: t('bulkDonorScheduling.Export'),
        field: 'export',
        initialWidth: 60,
        headerTooltip: 'No, Yes - Non EU, EU, AU, UK, SA (South America)',
      },
      {
        headerName: t('bulkDonorScheduling.TT'),
        field: 'trypsinTreat',
        initialWidth: 60,
        headerTooltip: 'Y, N',
      },
      {
        headerName: t('bulkDonorScheduling.Non_EU_Country'),
        field: 'nonEuCountry',
        initialWidth: 60,
        headerTooltip:
          'Argentina, Australia, Bolivia, Brazil, Canada, China, Columbia, Costa Rica, Ecuador, Guatemala, Honduras, Japan, Kenya, Mexico, New Zealand, Panama, Paraguay, Phillippines, United Kingdom, Other',
      },
    ],
    [
      t,
      fixBulkDonorScheduleDateFormat,
      searchYardsMemo,
      fertilizationSiteId,
      searchSchedulesMemo,
      searchShipmentGroupsMemo,
    ],
  );
}
